import { useEffect, useState } from 'react'
import { fxTorpedo } from '../../assets/index.js'
import { useAnimation } from '../../hooks/useAnimation.js'
import { useAudio } from '../../hooks/useAudio.js'
import { CELL_SIZE } from '../Grid/Cell.js'
import type { CellResult, Direction } from '@fleetduel/shared'

interface TorpedoFXProps {
  /** Grid-relative pixel position of cell 1 */
  x1: number
  y1: number
  /** Grid-relative pixel position of cell 2 (if exists) */
  x2?: number
  y2?: number
  cells: CellResult[]
  direction: Direction
  /** Called per cell when its impact resolves */
  onCellImpact: (index: number, hit: boolean) => void
  onDone: () => void
}

type Phase = 'launch' | 'travel' | 'impact1' | 'impact2' | 'done'

/**
 * Torpedo animation: launch → travel → sequential cell impacts.
 * ART_GUIDE_v2 §2.3. Total ~1.2s.
 */
export function TorpedoFX({ x1, y1, x2: _x2, y2: _y2, cells, direction, onCellImpact, onDone }: TorpedoFXProps) {
  const { phaseTimer, reduceMotion } = useAnimation()
  const { play, loop, stopAll } = useAudio()
  const [phase, setPhase] = useState<Phase>('launch')

  // Rotation for the torpedo sprite
  const rotation = direction === 'horizontal' ? 0 : 90

  useEffect(() => {
    if (reduceMotion) {
      cells.forEach((c, i) => onCellImpact(i, c.hit))
      onDone()
      return
    }

    // Phase 1: Launch (200ms) — play torpedo-launch sound
    play('torpedo-launch', 'combat')
    phaseTimer(200, () => {
      setPhase('travel')
      // Phase 2: Travel (400ms) — loop torpedo-travel sound
      const stopTravel = loop('torpedo-travel', 'combat')
      phaseTimer(400, () => {
        stopTravel()
        setPhase('impact1')
        // Play hit or miss sound for cell 1
        play(cells[0].hit ? 'strike-hit' : 'strike-miss', 'combat')
        onCellImpact(0, cells[0].hit)
        // Phase 3: Cell 1 impact (300ms)
        phaseTimer(300, () => {
          if (cells.length > 1) {
            setPhase('impact2')
            // Play hit or miss sound for cell 2
            play(cells[1].hit ? 'strike-hit' : 'strike-miss', 'combat')
            onCellImpact(1, cells[1].hit)
            // Phase 4: Cell 2 impact (300ms)
            phaseTimer(300, () => {
              setPhase('done')
              onDone()
            })
          } else {
            setPhase('done')
            onDone()
          }
        })
      })
    })

    return () => stopAll()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === 'done') return null

  // Compute travel start position (edge of grid)
  const gridEdgeX = direction === 'horizontal' ? -CELL_SIZE : x1
  const gridEdgeY = direction === 'vertical' ? -CELL_SIZE : y1

  const showProjectile = phase === 'launch' || phase === 'travel'

  return (
    <>
      {showProjectile && (
        <img
          src={fxTorpedo}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            left: phase === 'launch' ? gridEdgeX : undefined,
            top: phase === 'launch' ? gridEdgeY : undefined,
            width: CELL_SIZE * 2,
            height: CELL_SIZE / 2,
            pointerEvents: 'none',
            zIndex: 12,
            transform: `rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            transition: phase === 'travel' ? 'left 400ms linear, top 400ms linear' : undefined,
            ...(phase === 'travel' ? { left: x1, top: y1 + CELL_SIZE / 4 } : {}),
            animation: phase === 'launch' ? 'torpedo-enter 200ms ease-out forwards' : undefined,
          }}
        />
      )}
    </>
  )
}
